import React, { useState, useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useTranslation } from 'react-i18next';
import Tippy from '@tippy.js/react';
import { GeneralInput } from 'sharedComponents';
import { IconDragEqual } from 'svgIcons/MotionPortalIcons';
import { getAssetIcon } from 'helpers/icons';
import { PHYSICAL_CONNECTION_UI, CURSOR_STYLE, TOUCH_ACTION } from '../constants';
import { MAX_SPEED_RATIO_LENGTH } from 'helpers/constants';
import './ValidationTooltip.scss';

/**
 * SortableAssetItem - A draggable and sortable asset item component
 * Used in both selected assets and physical connection panels
 * Supports validation states and output ratio input for Gearbox assets in Selected Assets panel
 */
const SortableAssetItem = ({
    asset,
    isDragging = false,
    showDragIndicator = true,
    validationStatus = null,
    onOutputRatioChange = null,
    isInSelectedAssets = false,
    onRemoveGearbox = null
}) => {
    const { t: translate } = useTranslation();
    const [localOutputRatio, setLocalOutputRatio] = useState(asset.outputSpeedRatio || '');

    // Sync local state with asset prop changes
    useEffect(() => {
        setLocalOutputRatio(asset.outputSpeedRatio || '');
    }, [asset.outputSpeedRatio]);

    // Check if asset has invalid asset type error
    const hasInvalidAssetType = validationStatus?.errors?.some(err => err.type === 'INVALID_ASSET_TYPE') || false;

    // Determine if asset should be draggable
    // In Physical Connection: Always allow dragging (even with errors/warnings) so users can resolve conflicts
    // In Selected Assets: Only disable if connected to ANOTHER powertrain (not same powertrain) or invalid type
    const isDraggable = isInSelectedAssets
        ? !asset?.isConnectedToPowertrain && !hasInvalidAssetType
        : !hasInvalidAssetType; // In physical connection, always allow drag except for invalid asset types

    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging: isSortableDragging,
    } = useSortable({
        id: asset.id,
        disabled: !isDraggable // Disable dragging for connected assets (in selected panel) and invalid asset types
    });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging || isSortableDragging ? 0.5 : 1,
        cursor: isDraggable ? CURSOR_STYLE.DRAGGABLE : CURSOR_STYLE.NOT_DRAGGABLE,
        touchAction: isDraggable ? TOUCH_ACTION.DRAGGABLE : TOUCH_ACTION.NOT_DRAGGABLE
    };

    // Handle output ratio input change
    const handleOutputRatioChange = useCallback((value) => {
        // Only allow numeric input with optional decimal point
        if (value === '' || /^\d*\.?\d*$/.test(value)) {
            setLocalOutputRatio(value);
            if (onOutputRatioChange) {
                onOutputRatioChange(asset.assetId, value);
            }
        }
    }, [asset.assetId, onOutputRatioChange]);

    // Prevent Enter key from submitting form and causing page hang/duplicates
    const handleKeyDown = useCallback((event) => {
        if (event?.key === 'Enter') {
            event.preventDefault();
            event.stopPropagation();
            // Blur the input to trigger validation
            event?.target?.blur();
        }
    }, []);

    // Determine validation styling
    const getValidationClass = () => {
        if (!validationStatus) { return ''; }
        // Check if this is an INVALID_ASSET_TYPE error (should show yellow like connected assets)
        if (validationStatus.errors?.some(err => err.type === 'INVALID_ASSET_TYPE')) {
            return 'invalid-asset-type';
        }
        if (validationStatus.hasErrors) { return 'has-errors'; }
        if (validationStatus.hasWarnings) { return 'has-warnings'; }
        return '';
    };

    const renderValidationIcon = () => {
        // For assets already connected to powertrain, show yellow info icon
        if (asset?.isConnectedToPowertrain) {
            const baseMessage = translate('ABB.Powertrain.Frontend.ptToolsPowertrainWizard_Validation_AssetAlreadyConnected');
            const powertrainName = asset?.connectedPowertrainName;

            // Build tooltip content with powertrain name if available
            const tooltipContent = powertrainName
                ? `${baseMessage} - ${powertrainName}`
                : baseMessage;

            const icon =
                <svg className='validation-icon info' width={PHYSICAL_CONNECTION_UI.VALIDATION_ICON_SIZE.width} height={PHYSICAL_CONNECTION_UI.VALIDATION_ICON_SIZE.height} fill='currentColor' viewBox='0 0 20 20'>
                    <path fillRule='evenodd' d='M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z' clipRule='evenodd' />
                </svg>;

            return (
                <Tippy
                    content={tooltipContent}
                    interactiveBorder={10}
                    animation='scale'
                    theme='light-border'
                    trigger='click'
                    placement='top'
                    maxWidth={350}
                >
                    <div className='validation-icon-wrapper'>
                        {icon}
                    </div>
                </Tippy>
            );
        }

        if (!validationStatus) {
            return null;
        }

        // Check if this is an INVALID_ASSET_TYPE error (should show as yellow info icon)
        const hasInvalidAssetTypeError = validationStatus.errors?.some(err => err.type === 'INVALID_ASSET_TYPE');

        if (hasInvalidAssetTypeError) {
            const invalidTypeError = validationStatus?.errors?.find(err => err?.type === 'INVALID_ASSET_TYPE');
            const icon =
                <svg className='validation-icon info' width={PHYSICAL_CONNECTION_UI.VALIDATION_ICON_SIZE.width} height={PHYSICAL_CONNECTION_UI.VALIDATION_ICON_SIZE.height} fill='currentColor' viewBox='0 0 20 20' aria-hidden='true'>
                    <path fillRule='evenodd' d='M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l6.518 11.591c.75 1.334-.213 2.99-1.742 2.99H3.48c-1.53 0-2.492-1.656-1.743-2.99L8.257 3.1zM10 6a.75.75 0 00-.75.75v4.5a.75.75 0 001.5 0v-4.5A.75.75 0 0010 6zm0 8a1 1 0 100-2 1 1 0 000 2z' clipRule='evenodd' />
                </svg>;

            return (
                <Tippy
                    content={invalidTypeError.message}
                    interactiveBorder={10}
                    animation='scale'
                    theme='light-border'
                    trigger='click'
                    placement='top'
                    maxWidth={350}
                >
                    <div className='validation-icon-wrapper'>
                        {icon}
                    </div>
                </Tippy>
            );
        }

        // Build tooltip content from all errors and warnings
        const tooltipContent = () => {
            const messages = [
                ...validationStatus.errors || [],
                ...validationStatus.warnings || []
            ];

            if (messages.length === 0) {
                return null;
            }

            return (
                <div className='validation-tooltip-content'>
                    {messages.map((msg) =>
                        <div key={`${msg.type || 'msg'}-${msg.message}`} className='validation-tooltip-message'>
                            {msg.message}
                        </div>
                    )}
                </div>
            );
        };

        const hasTooltip = validationStatus.errors?.length > 0 || validationStatus.warnings?.length > 0;

        if (validationStatus.hasErrors) {
            const icon =
                <svg className='validation-icon error' width={PHYSICAL_CONNECTION_UI.VALIDATION_ICON_SIZE.width} height={PHYSICAL_CONNECTION_UI.VALIDATION_ICON_SIZE.height} fill='currentColor' viewBox='0 0 20 20'>
                    <path fillRule='evenodd' d='M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z' clipRule='evenodd' />
                </svg>;

            // Wrap with Tippy if we have error messages
            if (hasTooltip) {
                return (
                    <Tippy
                        content={tooltipContent()}
                        interactiveBorder={10}
                        animation='scale'
                        theme='light-border'
                        trigger='click'
                        placement='top'
                        maxWidth={350}
                    >
                        <div className='validation-icon-wrapper'>
                            {icon}
                        </div>
                    </Tippy>
                );
            }

            return icon;
        }

        if (validationStatus.hasWarnings) {
            const icon =
                <svg className='validation-icon warning' width={PHYSICAL_CONNECTION_UI.VALIDATION_ICON_SIZE.width} height={PHYSICAL_CONNECTION_UI.VALIDATION_ICON_SIZE.height} fill='currentColor' viewBox='0 0 20 20'>
                    <path fillRule='evenodd' d='M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z' clipRule='evenodd' />
                </svg>;

            // Wrap with Tippy if we have warning messages
            if (hasTooltip) {
                return (
                    <Tippy
                        content={tooltipContent()}
                        interactiveBorder={10}
                        animation='scale'
                        theme='light-border'
                        trigger='click'
                        placement='top'
                        maxWidth={350}
                    >
                        <div className='validation-icon-wrapper'>
                            {icon}
                        </div>
                    </Tippy>
                );
            }

            return icon;
        }
        return null;
    };

    const renderOutputRatioField = () => {
        // Show in Selected Assets panel for Gearbox assets
        // Also show in Physical Connection panel for Gearbox assets (with or without output ratio)
        const shouldShowInSelectedAssets = isInSelectedAssets && asset.selectedSubType === 'Gearbox';
        const shouldShowInPhysicalConnection = !isInSelectedAssets && asset.selectedSubType === 'Gearbox';

        if (!shouldShowInSelectedAssets && !shouldShowInPhysicalConnection) {
            return null;
        }

        // Use simple style for Selected Assets Panel
        if (isInSelectedAssets) {
            return (
                <div className='output-ratio-inline'>
                    <GeneralInput
                        className='ratio-input'
                        id={`output-ratio-${asset.assetId}`}
                        placeholder={translate('ABB.Powertrain.Frontend.ptToolsPowertrainWizard_PhysicalConnection_OutputSpeedRatioLabel')}
                        value={localOutputRatio}
                        onChange={handleOutputRatioChange}
                        onKeyDown={handleKeyDown}
                        width='120px'
                        maxLength={MAX_SPEED_RATIO_LENGTH}
                        aria-label={translate('ABB.Powertrain.Frontend.ptToolsPowertrainWizard_PhysicalConnection_OutputSpeedRatioLabel')}
                    />
                </div>
            );
        }

        // Use styled field with label and separator for Physical Connection Panel
        return (
            <div className='output-ratio-field'>
                <label className='ratio-label'>
                    {translate('ABB.Powertrain.Frontend.ptToolsPowertrainWizard_PhysicalConnection_OutputSpeedRatioLabel')}
                </label>
                <div className='ratio-separator'></div>
                <GeneralInput
                    className='ratio-input-styled'
                    id={`output-ratio-${asset.assetId}`}
                    placeholder='0.5'
                    value={localOutputRatio}
                    onChange={handleOutputRatioChange}
                    onKeyDown={handleKeyDown}
                    width='80px'
                    maxLength={MAX_SPEED_RATIO_LENGTH}
                    aria-label={translate('ABB.Powertrain.Frontend.ptToolsPowertrainWizard_PhysicalConnection_OutputSpeedRatioLabel')}
                    disabled={!onOutputRatioChange}
                />
            </div>
        );
    };

    // Helper function to get tooltip content for the asset icon
    const getAssetTooltipContent = () => {
        // For General Machine, show subtype name if mapped
        if (asset.type?.toLowerCase() === 'genericmachine' && asset.selectedSubType) {
            return asset.selectedSubType;
        }
        // Otherwise, show section and asset type name
        const typeName = asset.originalAsset?.assetTypeName || asset.type || '';
        return typeName;
    };

    return (
        <div
            ref={setNodeRef}
            style={style}
            className={`asset-item ${isDragging || isSortableDragging ? 'dragging' : ''} ${getValidationClass()} ${asset.isConnectedToPowertrain ? 'connected-asset' : ''} ${asset.isUnmonitoredGearbox ? 'unmonitored-gearbox' : ''}`}
            {...attributes}
            {...(isDraggable ? listeners : {})}
        >
            <div className='asset-content'>
                <div className='asset-header'>
                    <Tippy
                        content={getAssetTooltipContent()}
                        interactiveBorder={10}
                        animation='scale'
                        theme='light-border'
                        trigger='click'
                        placement='top'
                        disabled={!getAssetTooltipContent()}
                    >
                        <div className='asset-icon'>
                            {getAssetIcon(
                                asset.selectedSubType || asset.type,
                                { ...PHYSICAL_CONNECTION_UI.ICON_SIZES.EXTRA_LARGE, color: '#000000' }
                            )}
                        </div>
                    </Tippy>
                    <div className='asset-details'>
                        <div className='asset-name'>{asset.name}</div>
                        {asset?.isUnmonitoredGearbox && !isInSelectedAssets
                            ? <div className='asset-unmonitored-label'>{translate('ABB.Powertrain.Frontend.ptToolsPowertrainWizard_AddGearbox_UnmonitoredAssetInfoLabel')}</div>
                            : !asset.isUnmonitoredGearbox && <div className='asset-id'>({asset.assetId})</div>
                        }
                    </div>
                    <div className='asset-controls'>
                        {/* Show validation icon on left for Selected Assets, on right for Physical Connection */}
                        {isInSelectedAssets && renderValidationIcon()}

                        {renderOutputRatioField()}
                        {!isInSelectedAssets && renderValidationIcon()}
                        {asset?.isUnmonitoredGearbox && onRemoveGearbox &&
                            <button
                                className='remove-gearbox-button'
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onRemoveGearbox(asset.assetId);
                                }}
                                type='button'
                                title={translate('ABB.Powertrain.Frontend.ptToolsPowertrainWizard_PhysicalConnection_RemoveGearboxTooltip')}
                            >
                                <svg width='14' height='14' viewBox='0 0 20 20' fill='currentColor'>
                                    <path fillRule='evenodd' d='M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z' clipRule='evenodd' />
                                </svg>
                            </button>
                        }
                        {showDragIndicator && isDraggable &&
                            <div className='asset-drag-equal'>
                                <IconDragEqual width={12} height={12} />
                            </div>
                        }
                    </div>
                </div>
            </div>
        </div>
    );
};

SortableAssetItem.propTypes = {
    asset: PropTypes.shape({
        id: PropTypes.string.isRequired,
        name: PropTypes.string.isRequired,
        type: PropTypes.string.isRequired,
        assetId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
        selectedSubType: PropTypes.string,
        outputSpeedRatio: PropTypes.string,
        isConnectedToPowertrain: PropTypes.bool,
        connectedPowertrainName: PropTypes.string,
        isUnmonitoredGearbox: PropTypes.bool
    }).isRequired,
    isDragging: PropTypes.bool,
    showDragIndicator: PropTypes.bool,
    validationStatus: PropTypes.shape({
        hasErrors: PropTypes.bool,
        hasWarnings: PropTypes.bool,
        errors: PropTypes.array,
        warnings: PropTypes.array,
        requiresOutputRatio: PropTypes.bool,
        outputRatio: PropTypes.string
    }),
    onOutputRatioChange: PropTypes.func,
    isInSelectedAssets: PropTypes.bool,
    onRemoveGearbox: PropTypes.func
};

export default SortableAssetItem;
